import React from 'react';
import { Card, Stack, Group, Text, Button, Box, ThemeIcon } from '@mantine/core';
import { IconCalendarPlus, IconSparkles } from '@tabler/icons-react';

type NoActiveMealPlanCTAProps = {
  onStartPlanning: () => void;
  isStartPlanningDisabled: boolean;
  startPlanningTooltipText?: string;
  familySize?: { adults: number; kids: number };
};

export const NoActiveMealPlanCTA: React.FC<NoActiveMealPlanCTAProps> = ({
  onStartPlanning,
  isStartPlanningDisabled,
  startPlanningTooltipText,
  familySize
}) => {
  // Determine if user is cooking for multiple people or just themselves
  const totalPeople = (familySize?.adults || 0) + (familySize?.kids || 0);
  const audienceText = totalPeople > 1 ? "your family's" : 'your';
  return (
    <Card 
      radius="xl"
      style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        border: 'none',
        overflow: 'visible',
        position: 'relative'
      }}
      p={0}
    >
      {/* Decorative Elements */}
      <Box
        style={{
          position: 'absolute',
          top: -10,
          right: -10,
          width: 40,
          height: 40,
          background: 'rgba(255, 255, 255, 0.2)',
          borderRadius: '50%',
          zIndex: 1
        }}
      />
      <Box
        style={{
          position: 'absolute',
          bottom: -5,
          left: -5,
          width: 30,
          height: 30,
          background: 'rgba(255, 255, 255, 0.15)',
          borderRadius: '50%',
          zIndex: 1
        }}
      />

      <Stack gap={0}>
        {/* Header Section */}
        <Box p="xl" pb="lg" style={{ position: 'relative', zIndex: 2 }}>
          <Group justify="center" align="center">
            <Stack gap="xs" align="center">
              <Group gap="sm" align="center" wrap="nowrap">
                <ThemeIcon size={32} radius="md" variant="white" color="violet" style={{ flexShrink: 0 }}>
                  <IconCalendarPlus size={20} />
                </ThemeIcon>
                <Text fw={700} size="xl" c="white" ta="center">
                  Ready to plan ahead?
                </Text>
              </Group>
              <Text size="sm" c="rgba(255,255,255,0.8)" fw={500} ta="center" mb="md">
                Create next week's personalized meal plan
              </Text>
            </Stack>
          </Group>
        </Box>

        {/* Main Content Section */}
        <Box 
          p="xl" 
          pt="lg"
          style={{ 
            borderTopLeftRadius: 'var(--mantine-radius-xl)',
            borderTopRightRadius: 'var(--mantine-radius-xl)',
            marginTop: -16,
            background: 'white',
            position: 'relative',
            zIndex: 2
          }}
        >
          <Stack gap="xl" align="center" py="lg">
            <Stack gap="md" align="center" maw={400}>
              <Text fw={600} size="lg" c="dark" ta="center">
                Plan {audienceText} next delicious week
              </Text>
              <Text size="sm" c="dimmed" ta="center" style={{ lineHeight: 1.5 }}>
                Get personalized meal recommendations tailored to {audienceText} tastes, dietary needs, and preferences. Perfect for planning ahead and staying organized.
              </Text>
            </Stack>

            <Stack gap="sm" w="100%" maw={300}>
              <Button 
                size="lg"
                leftSection={<IconSparkles size={20} />}
                onClick={onStartPlanning}
                disabled={isStartPlanningDisabled}
                style={{ 
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none'
                }}
                fullWidth
              >
                {isStartPlanningDisabled ? 'Planning limit reached' : 'Start planning'}
              </Button>
              
              {isStartPlanningDisabled && startPlanningTooltipText && (
                <Text size="xs" c="dimmed" ta="center" style={{ lineHeight: 1.3 }}>
                  {startPlanningTooltipText}
                </Text>
              )}
            </Stack>

          </Stack>
        </Box>
      </Stack>
    </Card>
  );
};
